import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import Loader from "@/components/utils/loader";
import { useStore } from "@/store";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  fetchProblemTypeQuestion,
  fetchRolesWithProblemTypeVisibility,
  updateRoleProblemTypeVisibility,
} from "@/services/role-problem-type-visibility";

const ProblemTypeVisibilitySettings = () => {
  const { t } = useTranslation();
  const loggedInUser = useStore((state) => state.loggedInUser);
  const selectedLanguage = useStore((state) => state.selectedLanguage || "es");

  const isSuperadmin = Array.isArray(loggedInUser?.roles)
    ? loggedInUser.roles.includes("superadmin")
    : false;

  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState([]);
  const [problemTypeQuestion, setProblemTypeQuestion] = useState(null);
  const [savingRoleIds, setSavingRoleIds] = useState([]);
  const [isSavingAll, setIsSavingAll] = useState(false);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    if (!isSuperadmin) {
      setLoading(false);
      return;
    }

    const loadData = async () => {
      setLoading(true);
      setLoadError("");
      try {
        const [question, rolesData] = await Promise.all([
          fetchProblemTypeQuestion(),
          fetchRolesWithProblemTypeVisibility(),
        ]);

        setProblemTypeQuestion(question || null);
        setRoles(Array.isArray(rolesData) ? rolesData : []);
      } catch (error) {
        console.error("Error loading problem type visibility data:", error);
        setLoadError(t("errors.errorTitle"));
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [isSuperadmin, t]);

  const getOptionLabel = (option) => {
    if (!option) return "";
    const texts = option.text;

    if (Array.isArray(texts)) {
      const byLang = texts.find((item) => item.language === selectedLanguage);
      if (byLang?.text) return byLang.text;
      if (texts[0]?.text) return texts[0].text;
    }

    if (typeof texts === "string") {
      return texts;
    }

    return option.value || "";
  };

  const handleToggleMode = (roleId, restricted) => {
    setRoles((prev) =>
      prev.map((role) => {
        if (role.id !== roleId) return role;
        const nextMode = restricted ? "restricted" : "all";
        return {
          ...role,
          mode: nextMode,
          selectedProblemTypes:
            nextMode === "all" ? [] : role.selectedProblemTypes || [],
        };
      }),
    );
  };

  const handleToggleProblemType = (roleId, value, checked) => {
    setRoles((prev) =>
      prev.map((role) => {
        if (role.id !== roleId) return role;
        const current = Array.isArray(role.selectedProblemTypes)
          ? role.selectedProblemTypes
          : [];
        let next;
        if (checked) {
          next = current.includes(value) ? current : [...current, value];
        } else {
          next = current.filter((item) => item !== value);
        }
        return { ...role, selectedProblemTypes: next };
      }),
    );
  };

  const handleSaveRole = async (role) => {
    const isRestricted = role.mode === "restricted";
    const selected = Array.isArray(role.selectedProblemTypes)
      ? role.selectedProblemTypes
      : [];

    if (isRestricted && selected.length === 0) {
      toast.error(t("problemTypeVisibility.saveError"));
      return;
    }

    try {
      setSavingRoleIds((prev) => [...prev, role.id]);
      await updateRoleProblemTypeVisibility(
        role.id,
        isRestricted ? selected : [],
      );
      toast.success(t("problemTypeVisibility.saveSuccess"));
    } catch (error) {
      console.error("Error updating role problemTypeVisibility:", error);
      toast.error(t("problemTypeVisibility.saveError"));
      throw error;
    } finally {
      setSavingRoleIds((prev) => prev.filter((id) => id !== role.id));
    }
  };

  const handleSaveAll = async () => {
    if (!Array.isArray(roles) || roles.length === 0) {
      return;
    }

    const hasInvalidRestrictedRole = roles.some((role) => {
      const isRestricted = role.mode === "restricted";
      const selected = Array.isArray(role.selectedProblemTypes)
        ? role.selectedProblemTypes
        : [];
      return isRestricted && selected.length === 0;
    });

    if (hasInvalidRestrictedRole) {
      toast.error(t("problemTypeVisibility.saveError"));
      return;
    }

    try {
      setIsSavingAll(true);

      for (const role of roles) {
        const isRestricted = role.mode === "restricted";
        const selected = Array.isArray(role.selectedProblemTypes)
          ? role.selectedProblemTypes
          : [];

        await updateRoleProblemTypeVisibility(
          role.id,
          isRestricted ? selected : [],
        );
      }

      toast.success(t("problemTypeVisibility.saveSuccess"));
    } catch (error) {
      console.error("Error updating roles problemTypeVisibility:", error);
      toast.error(t("problemTypeVisibility.saveError"));
    } finally {
      setIsSavingAll(false);
    }
  };

  if (!isSuperadmin) {
    return (
      <section className="w-full flex justify-center">
        <Card className="p-6 w-full bg-slate-50">
          <p>{t("problemTypeVisibility.noPermission")}</p>
        </Card>
      </section>
    );
  }

  if (loading) {
    return (
      <section className="w-full flex justify-center">
        <Loader style={"loader"} />
      </section>
    );
  }

  if (loadError) {
    return (
      <section className="w-full flex justify-center">
        <Card className="p-6 w-full bg-slate-50">
          <p>{loadError}</p>
        </Card>
      </section>
    );
  }

  if (!problemTypeQuestion) {
    return (
      <section className="w-full flex justify-center">
        <Card className="p-6 w-full bg-slate-50">
          <h2 className="text-xl font-semibold mb-4">
            {t("problemTypeVisibility.title")}
          </h2>
          <p>{t("problemTypeVisibility.noQuestion")}</p>
        </Card>
      </section>
    );
  }

  const options = Array.isArray(problemTypeQuestion.options)
    ? problemTypeQuestion.options
    : [];

  if (options.length === 0) {
    return (
      <section className="w-full flex justify-center">
        <Card className="p-6 w-full bg-slate-50">
          <h2 className="text-xl font-semibold mb-4">
            {t("problemTypeVisibility.title")}
          </h2>
          <p>{t("problemTypeVisibility.noOptions")}</p>
        </Card>
      </section>
    );
  }

  return (
    <section className="w-full flex justify-center">
      <Card className="p-6 w-full bg-slate-50">
        <h2 className="text-xl font-semibold mb-2">
          {t("problemTypeVisibility.title")}
        </h2>
        <p className="mb-6 text-sm text-slate-600">
          {t("problemTypeVisibility.description")}
        </p>

        <div className="space-y-6">
          {roles.map((role) => {
            const isSaving = savingRoleIds.includes(role.id);
            const isRestricted = role.mode === "restricted";

            return (
              <div
                key={role.id}
                className="border border-slate-200 rounded-md p-4"
              >
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="font-semibold">{role.name}</h3>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        checked={!isRestricted}
                        onCheckedChange={(checked) =>
                          handleToggleMode(role.id, !checked)
                        }
                        id={`mode-all-${role.id}`}
                      />
                      <label
                        htmlFor={`mode-all-${role.id}`}
                        className="text-sm cursor-pointer"
                      >
                        {t("problemTypeVisibility.allTypesLabel")}
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        checked={isRestricted}
                        onCheckedChange={(checked) =>
                          handleToggleMode(role.id, !!checked)
                        }
                        id={`mode-restricted-${role.id}`}
                      />
                      <label
                        htmlFor={`mode-restricted-${role.id}`}
                        className="text-sm cursor-pointer"
                      >
                        {t("problemTypeVisibility.restrictedLabel")}
                      </label>
                    </div>
                  </div>
                </div>

                {isRestricted && (
                  <div className="mt-4 space-y-2">
                    {options.map((option) => {
                      const value = option.value;
                      const checked = Array.isArray(
                        role.selectedProblemTypes,
                      )
                        ? role.selectedProblemTypes.includes(value)
                        : false;

                      return (
                        <div
                          key={option.id ?? value}
                          className="flex items-center space-x-2"
                        >
                          <Checkbox
                            checked={checked}
                            onCheckedChange={(checkedValue) =>
                              handleToggleProblemType(
                                role.id,
                                value,
                                !!checkedValue,
                              )
                            }
                            id={`role-${role.id}-opt-${option.id ?? value}`}
                          />
                          <label
                            htmlFor={`role-${role.id}-opt-${option.id ?? value}`}
                            className="text-sm cursor-pointer"
                          >
                            {getOptionLabel(option)}
                          </label>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
          <div className="pt-4 flex justify-end">
            <Button
              type="button"
              className="bg-primary hover:bg-primary-600"
              onClick={handleSaveAll}
              disabled={isSavingAll}
            >
              {isSavingAll ? (
                <Loader style={"buttonLoader"} />
              ) : (
                t("buttonTexts.saveChanges")
              )}
            </Button>
          </div>
        </div>
      </Card>
    </section>
  );
};

export default ProblemTypeVisibilitySettings;
